import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Inbox, Linkedin, Loader2, Lock, LogOut, Mail, RefreshCw, Trash2, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { EMAIL, LINKEDIN } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ma_admin_token";

const PROJECT_TYPES = [
  "Website & Product Copy",
  "SEO Editorial",
  "Lifecycle Email",
  "UX Writing",
  "Campaign / Seasonal",
  "Full Voice Program",
];

const BUDGETS = ["Under $2k", "$2k – $5k", "$5k – $10k", "$10k+"];

const inputCls =
  "w-full bg-transparent border border-ink/20 focus:border-wine outline-none px-4 py-3.5 text-base transition-colors duration-300 placeholder:text-subtle/60";

const LoginModal = ({ open, onClose, onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      toast.success("Signed in. Welcome back, Melinda.");
      onSuccess(res.data.token);
      setEmail("");
      setPassword("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Sign-in failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[85] bg-ink/50 backdrop-blur-sm flex items-center justify-center p-6"
          onClick={onClose}
          data-testid="admin-login-backdrop"
        >
          <motion.form
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            className="bg-paper border border-line w-full max-w-sm p-8 shadow-[0_40px_80px_-30px_rgba(26,24,21,0.5)]"
            data-testid="admin-login-form"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="w-9 h-9 rounded-full bg-wine text-paper flex items-center justify-center">
                <Lock size={15} />
              </span>
              <button type="button" onClick={onClose} className="p-2 hover:text-wine transition-colors duration-300" data-testid="close-login-modal-button" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <h3 className="font-serif text-2xl tracking-tight">Melinda's Inbox</h3>
            <p className="mt-2 text-sm text-subtle leading-relaxed">
              Stored enquiries are private. Sign in to read them.
            </p>
            <div className="mt-6">
              <label htmlFor="admin-email" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-subtle mb-2">Email</label>
              <input id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="admin-login-email" autoComplete="username" />
            </div>
            <div className="mt-4">
              <label htmlFor="admin-password" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-subtle mb-2">Password</label>
              <input id="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="admin-login-password" autoComplete="current-password" />
            </div>
            {error && (
              <p className="mt-4 text-sm text-wine" data-testid="admin-login-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="admin-login-submit"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-ink text-paper font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-wine disabled:opacity-60 transition-colors duration-300"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={13} />}
              {busy ? "Signing in…" : "Unlock the Inbox"}
            </button>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const EnquiriesDrawer = ({ open, onClose, items, loading, onRefresh, onDelete, onLogout }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] bg-ink/50 backdrop-blur-sm"
        onClick={onClose}
        data-testid="enquiries-drawer-backdrop"
      >
        <motion.aside
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-paper border-l border-line flex flex-col"
          data-testid="enquiries-drawer"
        >
          <div className="flex items-center justify-between px-7 py-5 border-b border-line">
            <div className="flex items-center gap-3">
              <Inbox size={18} className="text-wine" />
              <h3 className="font-serif text-xl tracking-tight">Melinda's Inbox</h3>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={onRefresh} className="p-2 hover:text-wine transition-colors duration-300" data-testid="refresh-enquiries-button" aria-label="Refresh">
                <RefreshCw size={16} />
              </button>
              <button onClick={onLogout} className="p-2 hover:text-wine transition-colors duration-300" data-testid="admin-logout-button" aria-label="Sign out">
                <LogOut size={16} />
              </button>
              <button onClick={onClose} className="p-2 hover:text-wine transition-colors duration-300" data-testid="close-enquiries-drawer-button" aria-label="Close">
                <X size={18} />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-7 py-6">
            {loading ? (
              <div className="flex items-center gap-3 text-subtle" data-testid="enquiries-loading">
                <Loader2 size={16} className="animate-spin" /> Loading enquiries…
              </div>
            ) : items.length === 0 ? (
              <p className="font-serif italic text-lg text-subtle" data-testid="enquiries-empty">
                The inbox is quiet. The next brief could be yours.
              </p>
            ) : (
              <div className="flex flex-col gap-5" data-testid="enquiries-list">
                {items.map((e) => (
                  <div key={e.id} className="border border-line bg-cream p-5" data-testid={`enquiry-item-${e.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-serif text-lg leading-tight">{e.name}</div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-subtle mt-1">{e.email}</div>
                      </div>
                      <button onClick={() => onDelete(e.id)} className="p-1.5 text-subtle hover:text-wine transition-colors duration-300" data-testid={`delete-enquiry-${e.id}`} aria-label="Delete enquiry">
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="px-2.5 py-1 rounded-full bg-wine/10 text-wine font-mono text-[9px] uppercase tracking-[0.14em]">{e.project_type}</span>
                      {e.budget && (
                        <span className="px-2.5 py-1 rounded-full border border-ink/15 font-mono text-[9px] uppercase tracking-[0.14em] text-subtle">{e.budget}</span>
                      )}
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-ink/80">{e.message}</p>
                    <div className="mt-3 font-mono text-[9px] uppercase tracking-[0.14em] text-subtle">
                      {new Date(e.created_at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </motion.aside>
      </motion.div>
    )}
  </AnimatePresence>
);

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", project_type: PROJECT_TYPES[0], budget: BUDGETS[1], message: "" });
  const [sending, setSending] = useState(false);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loginOpen, setLoginOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const expireSession = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setDrawerOpen(false);
    toast.error("Session expired — please sign in again.");
  };

  const fetchEnquiries = async (tkn = token) => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${API}/enquiries`, { headers: { Authorization: `Bearer ${tkn}` } });
      setEnquiries(res.data);
    } catch (err) {
      if (err.response?.status === 401) expireSession();
      else toast.error("Could not load enquiries.");
    } finally {
      setLoadingList(false);
    }
  };

  const openDrawer = () => {
    if (token) {
      setDrawerOpen(true);
      fetchEnquiries();
    } else {
      setLoginOpen(true);
    }
  };

  const onLoginSuccess = (newToken) => {
    setToken(newToken);
    setLoginOpen(false);
    setDrawerOpen(true);
    fetchEnquiries(newToken);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setDrawerOpen(false);
    toast.success("Signed out. The inbox is locked.");
  };

  const deleteEnquiry = async (id) => {
    try {
      await axios.delete(`${API}/enquiries/${id}`, authHeaders());
      setEnquiries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Enquiry removed.");
    } catch (err) {
      if (err.response?.status === 401) expireSession();
      else toast.error("Could not delete enquiry.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent. Melinda replies within two business days.");
      setForm({ name: "", email: "", project_type: PROJECT_TYPES[0], budget: BUDGETS[1], message: "" });
    } catch {
      toast.error("Something went wrong — please try again or email directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 sm:py-32 lg:py-40" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 grid lg:grid-cols-12 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <p className="font-mono text-xs uppercase tracking-[0.24em] text-amber mb-6">Start a project</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-[1.15]">
            Let's make the words <em className="text-wine">do the work.</em>
          </h2>
          <p className="mt-6 text-base leading-relaxed text-subtle max-w-md">
            Tell me about the product, the audience, and the deadline. Every enquiry is read
            personally — expect a considered reply, not an autoresponder.
          </p>
          <a
            href={`mailto:${EMAIL}`}
            data-testid="contact-email-link"
            className="group mt-8 inline-flex items-center gap-3 font-serif text-xl text-ink hover:text-wine transition-colors duration-300"
          >
            <Mail size={18} className="text-wine" />
            {EMAIL}
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href={LINKEDIN}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-linkedin-link"
            className="group mt-4 inline-flex items-center gap-3 font-serif text-xl text-ink hover:text-wine transition-colors duration-300"
          >
            <Linkedin size={18} className="text-wine" />
            linkedin.com/in/melinda-adams
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <div className="mt-6 flex items-center gap-3">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-wine opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-wine" />
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-subtle">
              Typical response: within 2 business days
            </span>
          </div>
          <button
            onClick={openDrawer}
            data-testid="view-stored-enquiries-button"
            className="mt-12 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-ink/25 font-mono text-[11px] uppercase tracking-[0.18em] hover:border-wine hover:text-wine transition-colors duration-300"
          >
            {token ? <Inbox size={14} /> : <Lock size={13} />}
            {token ? "Open Stored Enquiries" : "Review Stored Enquiries"}
          </button>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          onSubmit={submit}
          className="lg:col-span-7 bg-cream border border-line p-8 sm:p-10"
          data-testid="contact-enquiry-form"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label htmlFor="contact-name" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-subtle mb-2">Your name</label>
              <input id="contact-name" required value={form.name} onChange={set("name")} placeholder="Jane Appleseed" className={inputCls} data-testid="contact-input-name" />
            </div>
            <div>
              <label htmlFor="contact-email" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-subtle mb-2">Email</label>
              <input id="contact-email" type="email" required value={form.email} onChange={set("email")} placeholder="jane@brand.com" className={inputCls} data-testid="contact-input-email" />
            </div>
            <div>
              <label htmlFor="contact-subject" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-subtle mb-2">Project type</label>
              <select id="contact-subject" value={form.project_type} onChange={set("project_type")} className={inputCls} data-testid="contact-input-subject">
                {PROJECT_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="contact-budget" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-subtle mb-2">Budget</label>
              <select id="contact-budget" value={form.budget} onChange={set("budget")} className={inputCls} data-testid="contact-input-budget">
                {BUDGETS.map((b) => <option key={b}>{b}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-6">
            <label htmlFor="contact-message" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-subtle mb-2">The brief, in your own words</label>
            <textarea id="contact-message" required rows={5} value={form.message} onChange={set("message")} placeholder="We're relaunching our product pages and need copy that explains technical features without losing the plot…" className={`${inputCls} resize-none`} data-testid="contact-input-message" />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-button"
            className="mt-8 inline-flex items-center gap-3 px-8 py-4 rounded-full bg-ink text-paper font-mono text-xs uppercase tracking-[0.18em] hover:bg-wine disabled:opacity-60 transition-colors duration-300"
          >
            {sending ? <Loader2 size={15} className="animate-spin" /> : <PenLineIcon />}
            {sending ? "Sending…" : "Send the Enquiry"}
          </button>
        </motion.form>
      </div>

      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} onSuccess={onLoginSuccess} />
      <EnquiriesDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        items={enquiries}
        loading={loadingList}
        onRefresh={() => fetchEnquiries()}
        onDelete={deleteEnquiry}
        onLogout={logout}
      />
    </section>
  );
};

const PenLineIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);
